<i18n locale="en">
{
  "labels": {
    "items": "Items",
    "text": "Text",
    "background": "Background"
  },
  "colors": {
    "white": "White",
    "beige": "Beige",
    "gray": "Gray",
    "black": "Black",
    "brand-primary": "Brand Primary"
  },
  "add-item": "Add item",
  "empty": "No items yet. Add one to get started."
}
</i18n>

<template>
  <div class="flex-col gap-8">
    <OwnInputContainer :label="t('labels.items')">
      <div class="flex-col gap-4">
        <div
          v-for="(item, index) in currentBlockParams.items"
          :key="index"
          class="scrolling-editor__item flex-row items-center gap-4"
          :data-testid="`scrolling-item-${index}`"
        >
          <OwnInput
            :model-value="item.text"
            :placeholder="t('labels.text')"
            data-testid="scrolling-item-text-input"
            @update:model-value="updateItemText(index, $event)"
          />
          <button
            type="button"
            class="scrolling-editor__delete-btn"
            :aria-label="`Remove item ${index + 1}`"
            data-testid="scrolling-item-remove-btn"
            @click="removeItem(index)"
          >
            <PhTrash :size="18" />
          </button>
        </div>
        <p v-if="currentBlockParams.items.length === 0" class="text-secondary">
          {{ t('empty') }}
        </p>
        <OwnButton
          :text="t('add-item')"
          type="secondary"
          data-testid="scrolling-add-item-btn"
          @click="addItem"
        />
      </div>
    </OwnInputContainer>
    <OwnInputContainer :label="t('labels.background')">
      <div class="scrolling-editor__color-tiles">
        <button
          v-for="token in backgroundColorOptions"
          :key="token"
          type="button"
          :aria-label="t(`colors.${token}`)"
          :data-testid="`scrolling-bg-color-${token}`"
          :class="[
            'scrolling-editor__color-tile',
            currentBlockParams.background?.params?.token === token &&
              'scrolling-editor__color-tile--selected',
          ]"
          :style="{ backgroundColor: tokenToCss(token) }"
          @click="updateBackgroundColor(token)"
        />
      </div>
    </OwnInputContainer>
  </div>
</template>

<script>
import { PhTrash } from '@phosphor-icons/vue'
import { useI18n } from 'vue-i18n'
import { mapGetters } from 'vuex'

import { useExtendedMergedThemeMixin } from '@/lib/theme/useExtendedMergedTheme'
import { OwnButton } from '@/ui/OwnButton'
import { OwnInput } from '@/ui/OwnInput'
import { OwnInputContainer } from '@/ui/OwnInputContainer'
import { tokenToCss, BACKGROUND_COLOR_TOKENS } from '@/utils/backgroundColors'

import { ScrollingCommands } from '../commands/ScrollingCommands'

export default {
  name: 'ScrollingEditor',
  components: {
    OwnButton,
    OwnInput,
    OwnInputContainer,
    PhTrash,
  },
  mixins: [useExtendedMergedThemeMixin],
  props: {
    id: { type: String, required: true },
  },
  setup() {
    const { t } = useI18n()
    return { t }
  },
  data() {
    return {
      backgroundColorOptions: BACKGROUND_COLOR_TOKENS,
    }
  },
  computed: {
    ...mapGetters({
      blockById: 'websiteBuilder/blockById',
    }),
    currentBlockParams() {
      return this.blockById(this.id)?.params
    },
  },
  methods: {
    async addItem() {
      const items = [...(this.currentBlockParams.items ?? [])]
      items.push({ text: '' })
      await this.applyUpdate({ items })
    },
    async applyUpdate(newValue) {
      await ScrollingCommands.updateBlock({
        targetId: this.id,
        update: {
          params: {
            ...newValue,
          },
        },
      })
    },
    async removeItem(index) {
      const items = [...(this.currentBlockParams.items ?? [])]
      items.splice(index, 1)
      await this.applyUpdate({ items })
    },
    tokenToCss(token) {
      return tokenToCss(token, this.mergedTheme)
    },
    async updateBackgroundColor(token) {
      await this.applyUpdate({
        background: {
          params: { token },
          type: 'color',
        },
      })
    },
    async updateItemText(index, text) {
      const items = [...(this.currentBlockParams.items ?? [])].map((item) => ({
        ...item,
      }))
      items[index] = { ...items[index], text }
      await this.applyUpdate({ items })
    },
  },
}
</script>

<style lang="scss" scoped>
.scrolling-editor {
  &__item {
    gap: 8px;
  }

  &__delete-btn {
    flex-shrink: 0;
    padding: 4px;
    color: var(--text-secondary);
    cursor: pointer;
    border: none;
    background: none;

    &:hover {
      color: var(--text-primary);
    }
  }

  &__color-tiles {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  &__color-tile {
    width: 54px;
    height: 54px;
    display: inline-block;
    border-radius: 12px;
    border: 1px solid var(--divider);
    cursor: pointer;
  }

  &__color-tile--selected {
    outline: 2px solid var(--misc-brand);
    outline-offset: 2px;
  }
}
</style>
